export type UnitType = "mass" | "volume" | "length" | "count";

interface UnitConversion {
  unit: string;
  ratio: number;
}

export const units: Record<UnitType, UnitConversion[]> = {
  mass: [
    { unit: "kg", ratio: 1 },
    { unit: "g", ratio: 0.001 },
    { unit: "mg", ratio: 0.000001 },
    { unit: "lb", ratio: 0.45359237 },
    { unit: "oz", ratio: 0.0283495231 },
  ],
  volume: [
    { unit: "l", ratio: 1 },
    { unit: "ml", ratio: 0.001 },
    { unit: "gal", ratio: 3.78541 },
    { unit: "qt", ratio: 0.946353 },
    { unit: "pt", ratio: 0.473176 },
    { unit: "cup", ratio: 0.236588 },
    { unit: "fl oz", ratio: 0.0295735 },
    { unit: "tbsp", ratio: 0.0147868 },
    { unit: "tsp", ratio: 0.00492892 },
  ],
  length: [
    { unit: "m", ratio: 1 },
    { unit: "cm", ratio: 0.01 },
    { unit: "mm", ratio: 0.001 },
    { unit: "in", ratio: 0.0254 },
    { unit: "ft", ratio: 0.3048 },
  ],
  count: [
    { unit: "pcs", ratio: 1 },
    { unit: "dozen", ratio: 12 },
    { unit: "case", ratio: 24 },
  ],
};

export function convertUnits(
  value: number,
  fromUnit: string,
  toUnit: string
): number | null {
  // If units are the same, return the original value
  if (fromUnit === toUnit) return value;

  // Find the unit types
  let fromUnitType: UnitType | undefined;
  let toUnitType: UnitType | undefined;
  let fromRatio = 0;
  let toRatio = 0;

  for (const [type, conversions] of Object.entries(units)) {
    const fromConversion = conversions.find((u) => u.unit === fromUnit);
    const toConversion = conversions.find((u) => u.unit === toUnit);

    if (fromConversion) {
      fromUnitType = type as UnitType;
      fromRatio = fromConversion.ratio;
    }
    if (toConversion) {
      toUnitType = type as UnitType;
      toRatio = toConversion.ratio;
    }
  }

  // If units are not of the same type or not found, return null
  if (!fromUnitType || !toUnitType || fromUnitType !== toUnitType) {
    return null;
  }

  // Convert to base unit then to target unit
  return (value * fromRatio) / toRatio;
}

export function getUnitType(unit: string): UnitType | undefined {
  for (const [type, conversions] of Object.entries(units)) {
    if (conversions.some((u) => u.unit === unit)) {
      return type as UnitType;
    }
  }
  return undefined;
}

export function isCompatibleUnit(unit1: string, unit2: string): boolean {
  const type1 = getUnitType(unit1);
  const type2 = getUnitType(unit2);
  return type1 !== undefined && type1 === type2;
}

export function getCompatibleUnits(unit: string): string[] {
  const type = getUnitType(unit);
  return type ? units[type].map((u) => u.unit) : [unit];
}
